import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getAssignmentReport, getFatherMembers, updateMember } from '../../services/api';
import { FiArrowRight, FiAlertTriangle, FiX, FiUsers, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import Toast from '../../components/Toast';
import DependentCard from '../../components/DependentCard';

const statusPill = { member: 'pill-green', inactive: 'pill-red', transferred: 'pill-blue', changed_religion: 'pill-gray', deceased: 'pill-gray', suspended: 'pill-red' };

const Assignment = () => {
  const { t } = useTranslation();
  const [data, setData] = useState({ fathers: [], unassigned: [] });
  const [movingMember, setMovingMember] = useState(null);
  const [selectedFatherId, setSelectedFatherId] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [expandedFather, setExpandedFather] = useState(null);
  const [fatherMembers, setFatherMembers] = useState({});
  const [expandedMember, setExpandedMember] = useState(null);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const load = () => getAssignmentReport().then(r => setData(r.data || { fathers: [], unassigned: [] })).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggleFather = (father) => {
    if (expandedFather === father.id) { setExpandedFather(null); return; }
    setExpandedFather(father.id);
    setExpandedMember(null);
    if (!fatherMembers[father.id]) {
      getFatherMembers(father.id).then(r => setFatherMembers(p => ({ ...p, [father.id]: r.data }))).catch(() => {});
    }
  };

  const handleMove = async () => {
    if (!selectedFatherId) return;
    setSaving(true);
    try {
      await updateMember(movingMember.id, { ...movingMember, spiritual_father_id: parseInt(selectedFatherId) });
      showToast(`${movingMember.first_name} ${movingMember.last_name} assigned successfully`);
      setMovingMember(null);
      setFatherMembers({});
      load();
    } catch (e) {
      showToast(e.response?.data?.error || 'Failed', 'error');
    } finally { setSaving(false); }
  };

  const maxCount = data.fathers.length > 0 ? Math.max(...data.fathers.map(f => f.member_count), 1) : 1;

  return (
    <div className="page-content">
      <div className="page-header">
        <div><h1>{t('nav.assignMembers')}</h1></div>
      </div>

      {/* Unassigned warning */}
      {data.unassigned.length > 0 && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '14px 18px', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <FiAlertTriangle style={{ color: '#d97706' }} />
            <strong style={{ fontSize: 13, color: '#92400e' }}>
              {data.unassigned.length} member{data.unassigned.length > 1 ? 's' : ''} not assigned to any spiritual father
            </strong>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {data.unassigned.map(m => (
              <div key={m.id} style={{ background: '#fff', border: '1px solid #fde68a', borderRadius: 8, padding: '6px 12px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                <strong>{m.first_name} {m.last_name}</strong>
                <span className={`pill ${statusPill[m.membership_status]}`} style={{ fontSize: 10 }}>{m.membership_status}</span>
                <button className="btn btn-sm btn-primary" onClick={() => { setMovingMember(m); setSelectedFatherId(''); }}>Assign</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Load overview */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">Load Balance Overview</div>
        <div className="card-body">
          {data.fathers.length === 0 ? (
            <div className="empty-state"><p>No spiritual fathers registered yet</p></div>
          ) : data.fathers.map(f => {
            const pct = Math.round((f.member_count / maxCount) * 100);
            const color = f.member_count === 0 ? '#9ca3af' : f.member_count <= 5 ? '#16a34a' : f.member_count <= 10 ? '#d97706' : '#dc2626';
            return (
              <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '8px 0', cursor: 'pointer' }} onClick={() => toggleFather(f)}>
                <div style={{ width: 180, flexShrink: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{f.first_name} {f.last_name}</div>
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>{f.member_count} {f.member_count === 1 ? 'member' : 'members'}</div>
                </div>
                <div style={{ flex: 1, height: 10, background: '#f3f4f6', borderRadius: 6, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct || 2}%`, background: color, borderRadius: 6, transition: 'width 0.4s' }} />
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, color, width: 70, textAlign: 'right' }}>{f.member_count} members</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Per-father member lists */}
      {data.fathers.map(father => (
        <div key={father.id} className="card" style={{ marginBottom: 16 }}>
          <div
            className="card-header"
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => toggleFather(father)}
          >
            <div>
              <strong>{father.first_name} {father.last_name}</strong>
              {father.phone && <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 10 }}>{father.phone}</span>}
            </div>
            <span className="pill pill-purple">{father.member_count} members</span>
          </div>

          {expandedFather === father.id && (
            <div style={{ overflowX: 'auto' }}>
              {!fatherMembers[father.id] ? (
                <div style={{ padding: 20, color: '#9ca3af', fontSize: 13 }}>Loading...</div>
              ) : fatherMembers[father.id].length === 0 ? (
                <div style={{ padding: 20, color: '#9ca3af', fontSize: 13 }}>No members assigned</div>
              ) : (
                <table>
                  <thead>
                    <tr><th></th><th>#</th><th>Name</th><th>Phone</th><th>Status</th><th>Dependents</th><th></th></tr>
                  </thead>
                  <tbody>
                    {fatherMembers[father.id].map((m, i) => {
                      const hasDeps = m.dependents && m.dependents.length > 0;
                      const isExpanded = expandedMember === `${father.id}-${m.id}`;
                      return (
                        <>
                          <tr key={m.id}
                            style={{ cursor: hasDeps ? 'pointer' : 'default', background: m.membership_status === 'inactive' ? '#fffbeb' : hasDeps ? '#f5f3ff' : 'transparent', borderLeft: hasDeps ? '3px solid #7c3aed' : '3px solid transparent' }}
                            onClick={() => hasDeps && setExpandedMember(isExpanded ? null : `${father.id}-${m.id}`)}>
                            <td style={{ width: 28, color: '#9ca3af' }}>
                              {hasDeps ? (isExpanded ? <FiChevronDown /> : <FiChevronRight />) : null}
                            </td>
                            <td>{i + 1}</td>
                            <td><strong>{m.title ? `${m.title} ` : ''}{m.first_name} {m.middle_name || ''} {m.last_name}</strong></td>
                            <td>{m.phone || '—'}</td>
                            <td><span className={`pill ${statusPill[m.membership_status]}`}>{m.membership_status}</span></td>
                            <td>
                              {hasDeps
                                ? <span className="pill pill-blue">{m.dependent_count} {m.dependent_count === 1 ? 'dependent' : 'dependents'}</span>
                                : <span style={{ color: '#9ca3af', fontSize: 12 }}>None</span>}
                            </td>
                            <td onClick={e => e.stopPropagation()}>
                              <button className="btn btn-sm btn-secondary" onClick={() => { setMovingMember(m); setSelectedFatherId(''); }}>
                                <FiArrowRight /> Move
                              </button>
                            </td>
                          </tr>
                          {isExpanded && hasDeps && (
                            <tr key={`${m.id}-deps`}>
                              <td colSpan={7} style={{ padding: 0, background: '#f8fafc' }}>
                                <div style={{ padding: '10px 48px 14px' }}>
                                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                                    Dependents under {m.first_name} {m.last_name}
                                  </div>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                                    {m.dependents.map(d => <DependentCard key={d.id} d={d} />)}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      ))}

      {/* Assign / Move Modal */}
      {movingMember && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <div>
                <h3>Assign Member</h3>
                <p>{movingMember.first_name} {movingMember.last_name}</p>
              </div>
              <button className="btn-icon" onClick={() => setMovingMember(null)}><FiX /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Assign to Spiritual Father <span className="required">*</span></label>
                <select value={selectedFatherId} onChange={e => setSelectedFatherId(e.target.value)}>
                  <option value="">— Select father —</option>
                  {data.fathers.map(f => (
                    <option key={f.id} value={f.id}>
                      {f.first_name} {f.last_name} ({f.member_count} members)
                    </option>
                  ))}
                </select>
              </div>
              {selectedFatherId && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#6b7280', marginTop: 4 }}>
                  <FiArrowRight />
                  Assigning <strong>{movingMember.first_name}</strong> to <strong>{data.fathers.find(f => String(f.id) === String(selectedFatherId))?.first_name}</strong>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setMovingMember(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleMove} disabled={saving || !selectedFatherId}>
                {saving ? 'Assigning...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast message={toast?.msg} type={toast?.type} onClose={() => setToast(null)} />
    </div>
  );
};

export default Assignment;
